import { useState, useEffect, useMemo, useCallback } from 'react';
import api from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Checkbox } from '../components/ui/checkbox';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Tooltip as UITooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../components/ui/tooltip';
import {
  GitCompare, CheckCircle2, BarChart3, Users, Download,
  TrendingUp, FlaskConical, Info, ArrowUpDown, FileSpreadsheet
} from 'lucide-react';
import { cn } from '../lib/utils';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, Legend, ResponsiveContainer, Cell, RadarChart,
  PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar
} from 'recharts';
import { getScaleColors, getOrderedScale } from '../lib/chartUtils';

/* ------------------------------------------------------------------ */
/*  Color palettes                                                     */
/* ------------------------------------------------------------------ */

// Distinct simulation colors (high contrast, not pastels)
const SIM_COLORS = [
  '#3B82F6', '#10B981', '#F59E0B', '#8B5CF6',
  '#EF4444', '#06B6D4', '#EC4899', '#14B8A6',
];

// Scale-aware color for a response option by position
const getResponseScaleColor = (responseOptions, responseValue) => {
  if (!responseOptions || responseOptions.length === 0) return '#94A3B8';
  const colors = getScaleColors(responseOptions);
  const idx = responseOptions.indexOf(responseValue);
  return idx >= 0 ? colors[idx] : '#94A3B8';
};

/* ------------------------------------------------------------------ */
/*  Sub-components                                                     */
/* ------------------------------------------------------------------ */

function SignificanceBadge({ pValue, cramersV }) {
  let label, color;
  if (pValue < 0.001) { label = 'p<0.001'; color = 'bg-emerald-100 dark:bg-emerald-500/20 text-emerald-800 dark:text-emerald-300 border-emerald-200 dark:border-emerald-500/30'; }
  else if (pValue < 0.01) { label = `p=${pValue.toFixed(3)}`; color = 'bg-emerald-50 dark:bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 border-emerald-200 dark:border-emerald-500/30'; }
  else if (pValue < 0.05) { label = `p=${pValue.toFixed(3)}`; color = 'bg-amber-50 dark:bg-amber-500/15 text-amber-700 dark:text-amber-400 border-amber-200 dark:border-amber-500/30'; }
  else { label = `p=${pValue.toFixed(3)}`; color = 'bg-slate-50 dark:bg-slate-800 text-slate-500 dark:text-slate-400 border-slate-200 dark:border-border'; }

  let effectLabel = '';
  if (cramersV >= 0.5) effectLabel = 'Large';
  else if (cramersV >= 0.3) effectLabel = 'Medium';
  else if (cramersV >= 0.1) effectLabel = 'Small';
  else effectLabel = 'Negligible';

  return (
    <div className="flex items-center gap-1.5">
      <span className={cn('px-1.5 py-0.5 rounded text-[10px] font-medium border', color)}>{label}</span>
      <span className="text-[10px] text-slate-400">{effectLabel} effect</span>
    </div>
  );
}

function StatCard({ label, value, sub, icon: Icon }) {
  return (
    <div className="flex items-center gap-3 p-3 rounded-lg bg-slate-50/80 dark:bg-muted/50 border border-slate-100 dark:border-border">
      {Icon && <Icon className="w-4 h-4 text-slate-400 flex-shrink-0" />}
      <div className="min-w-0">
        <p className="text-[11px] text-slate-500 dark:text-slate-400 uppercase tracking-wide">{label}</p>
        <p className="text-lg font-semibold text-slate-800 dark:text-slate-100 leading-tight">{value}</p>
        {sub && <p className="text-[11px] text-slate-400 mt-0.5">{sub}</p>}
      </div>
    </div>
  );
}

/* Custom tooltip for stacked bar */
function StackedTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-slate-900 text-white px-3 py-2 rounded-md text-xs shadow-lg">
      <p className="font-medium mb-1">{label}</p>
      {payload.filter(p => p.value > 0).map((p, i) => (
        <div key={i} className="flex items-center gap-2">
          <div className="w-2 h-2 rounded-full" style={{ backgroundColor: p.color || p.fill }} />
          <span>{p.name}: {p.value.toFixed(1)}%</span>
        </div>
      ))}
    </div>
  );
}

/* Custom tooltip for grouped bar */
function GroupedTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-slate-900 text-white px-3 py-2 rounded-md text-xs shadow-lg max-w-xs">
      <p className="font-medium mb-1">{label}</p>
      {payload.map((p, i) => (
        <div key={i} className="flex items-center gap-2">
          <div className="w-2 h-2 rounded-full" style={{ backgroundColor: p.fill }} />
          <span className="truncate">{p.name}: {Number(p.value).toFixed(1)}%</span>
        </div>
      ))}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Main Component                                                     */
/* ------------------------------------------------------------------ */
export default function Compare() {
  const [simulations, setSimulations] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [comparison, setComparison] = useState(null);
  const [demographicComparison, setDemographicComparison] = useState(null);
  const [availableDemographics, setAvailableDemographics] = useState([]);
  const [selectedDemographic, setSelectedDemographic] = useState('');
  const [loading, setLoading] = useState(true);
  const [comparing, setComparing] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [activeTab, setActiveTab] = useState('distribution');
  const [chartMode, setChartMode] = useState('stacked'); // stacked | grouped | radar

  // ---- Data fetching ----
  useEffect(() => { fetchSimulations(); }, []);

  const fetchAvailableDemographicsRef = useCallback(async () => {
    try {
      const r = await api.get('/compare/available-demographics', { params: { simulation_ids: selectedIds.join(',') } });
      const demos = r.data.demographics || [];
      setAvailableDemographics(demos);
      // Auto-preselect a sensible default if none chosen yet
      if (demos.length > 0) {
        setSelectedDemographic(prev => {
          if (prev && demos.includes(prev)) return prev;
          // Prefer "Age Group" → "Department" → first available
          const preferenceOrder = ['age_group', 'Age Group', 'department', 'Department', 'seniority', 'tenure'];
          const preferred = preferenceOrder.find(p => demos.includes(p));
          return preferred || demos[0];
        });
      }
    } catch (err) { console.debug('available-demographics fetch failed (silent):', err); }
  }, [selectedIds]);

  useEffect(() => {
    if (selectedIds.length >= 2) fetchAvailableDemographicsRef();
    else { setAvailableDemographics([]); setSelectedDemographic(''); setDemographicComparison(null); }
  }, [selectedIds, fetchAvailableDemographicsRef]);

  // Auto-load demographic comparison when both simulations + demographic are ready
  useEffect(() => {
    if (selectedIds.length >= 2 && selectedDemographic && !demographicComparison && !comparing) {
      // Fire-and-forget; user will see results appear automatically
      handleDemographicCompare();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedIds, selectedDemographic]);

  const fetchSimulations = async () => {
    try {
      const r = await api.get('/simulations');
      setSimulations(r.data.filter(s =>
        s.status === 'completed' || s.status === 'completed_with_warning' || s.status === 'completed_with_warnings'
      ));
    } catch (err) { console.debug('fetchSimulations failed (interceptor handles toast):', err); } finally { setLoading(false); }
  };

  const toggleSelection = (id) => {
    setSelectedIds(prev => prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]);
    setComparison(null);
    setDemographicComparison(null);
  };

  const handleCompare = async () => {
    if (selectedIds.length < 2) { toast.error('Select at least 2 simulations'); return; }
    setComparing(true);
    try {
      const r = await api.post('/compare', selectedIds);
      setComparison(r.data);
      setActiveTab('distribution');
    } catch (err) { console.debug('handleCompare failed (interceptor handles toast):', err); } finally { setComparing(false); }
  };

  const handleDemographicCompare = async () => {
    if (selectedIds.length < 2 || !selectedDemographic) { toast.error('Select simulations and a demographic'); return; }
    setComparing(true);
    try {
      const r = await api.post('/compare/demographic', { simulation_ids: selectedIds, demographic: selectedDemographic });
      setDemographicComparison(r.data);
      setActiveTab('demographic');
    } catch (err) { console.debug('handleCompare failed (interceptor handles toast):', err); } finally { setComparing(false); }
  };

  const handleExport = async (format = 'xlsx') => {
    setExporting(true);
    try {
      const r = await api.post('/compare/export', { simulation_ids: selectedIds, format }, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `comparison_export.${format}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Exported as ${format.toUpperCase()}`);
    } catch (_) { toast.error('Export failed'); } finally { setExporting(false); }
  };

  // ---- Derived data ----
  const responseOptions = useMemo(() => {
    if (!comparison?.comparisons?.[0]) return [];
    return comparison.comparisons[0].response_options || Object.keys(comparison.comparisons[0].percentages);
  }, [comparison]);

  const orderedOptions = useMemo(() => getOrderedScale(responseOptions), [responseOptions]);
  const scaleColors = useMemo(() => getScaleColors(orderedOptions), [orderedOptions]);

  // Stacked bar data: one row per simulation
  const stackedData = useMemo(() => {
    if (!comparison) return [];
    return comparison.comparisons.map(c => {
      const row = { name: c.simulation.name, id: c.simulation.id };
      orderedOptions.forEach(opt => { row[opt] = c.percentages[opt] || 0; });
      return row;
    });
  }, [comparison, orderedOptions]);

  // Grouped bar data: one row per response option, bars = simulations
  const groupedData = useMemo(() => {
    if (!comparison) return [];
    return orderedOptions.map(opt => {
      const row = { response: opt };
      comparison.comparisons.forEach(c => { row[c.simulation.name] = c.percentages[opt] || 0; });
      return row;
    });
  }, [comparison, orderedOptions]);

  // Radar data
  const radarData = useMemo(() => {
    if (!comparison) return [];
    return orderedOptions.map(opt => {
      const row = { option: opt };
      comparison.comparisons.forEach(c => { row[c.simulation.name] = c.percentages[opt] || 0; });
      return row;
    });
  }, [comparison, orderedOptions]);

  // Demographic chart data builder
  const getDemographicChartData = useCallback((demoValue) => {
    if (!demographicComparison) return [];
    const allResponses = new Set();
    demographicComparison.comparisons.forEach(c => {
      const bd = c.breakdown[demoValue];
      if (bd?.percentages) Object.keys(bd.percentages).forEach(r => allResponses.add(r));
    });
    return Array.from(allResponses).map(response => {
      const entry = { response };
      demographicComparison.comparisons.forEach(c => {
        entry[c.simulation.name] = c.breakdown[demoValue]?.percentages?.[response] || 0;
      });
      return entry;
    });
  }, [demographicComparison]);

  // ---- Render helpers ----
  if (loading) {
    return (
      <div data-testid="compare-page" className="space-y-6">
        <div className="h-8 w-48 skeleton" />
        <div className="grid gap-4">{[1, 2, 3].map(i => <div key={i} className="h-20 skeleton rounded-md" />)}</div>
      </div>
    );
  }

  if (simulations.length < 2) {
    return (
      <div data-testid="compare-page" className="space-y-6">
        <h1 className="text-3xl font-bold text-slate-900 dark:text-white tracking-tight">Compare Simulations</h1>
        <Card className="border border-slate-200/80 dark:border-border">
          <CardContent className="empty-state py-16">
            <GitCompare className="empty-state-icon" strokeWidth={1} />
            <h3 className="text-lg font-medium text-slate-900 dark:text-white mt-2">Not enough simulations</h3>
            <p className="text-slate-500 dark:text-slate-400 text-sm mt-1">You need at least 2 completed simulations to compare</p>
            <Button asChild className="mt-6 bg-primary hover:bg-primary/90"><Link to="/simulations">Go to Simulations</Link></Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const pairwiseTests = comparison?.pairwise_tests || [];

  return (
    <div data-testid="compare-page" className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 dark:text-white tracking-tight">Compare Simulations</h1>
          <p className="text-slate-500 dark:text-slate-400 mt-1">Select simulations to compare results side by side</p>
        </div>
        <div className="flex items-center gap-2">
          {comparison && (
            <div className="flex items-center gap-1.5">
              <Button
                size="sm" variant="outline"
                onClick={() => handleExport('xlsx')}
                disabled={exporting}
                data-testid="export-xlsx-btn"
                className="h-8 text-xs"
              >
                <FileSpreadsheet className="w-3.5 h-3.5 mr-1" />
                XLSX
              </Button>
              <Button
                size="sm" variant="outline"
                onClick={() => handleExport('csv')}
                disabled={exporting}
                data-testid="export-csv-btn"
                className="h-8 text-xs"
              >
                <Download className="w-3.5 h-3.5 mr-1" />
                CSV
              </Button>
            </div>
          )}
          <Button
            onClick={handleCompare}
            disabled={selectedIds.length < 2 || comparing}
            className="bg-primary hover:bg-primary/90 btn-press"
            data-testid="compare-btn"
          >
            <GitCompare className="w-4 h-4 mr-2" />
            {comparing ? 'Comparing...' : `Compare (${selectedIds.length})`}
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* ---- Selection Panel ---- */}
        <Card className="lg:col-span-1 border border-slate-200/80 dark:border-border">
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Select Simulations</CardTitle>
            <CardDescription className="text-xs">Choose 2 or more to compare</CardDescription>
          </CardHeader>
          <CardContent className="space-y-2 max-h-[560px] overflow-y-auto">
            {simulations.map((sim) => (
              <div
                key={sim.id}
                className={cn(
                  'flex items-center gap-3 p-3 rounded-md border cursor-pointer transition-all duration-150',
                  selectedIds.includes(sim.id)
                    ? 'border-blue-300 dark:border-blue-500/40 bg-blue-50/80 dark:bg-blue-500/10 shadow-sm'
                    : 'border-slate-200 dark:border-border hover:bg-slate-50/50 dark:hover:bg-white/[0.03]'
                )}
                onClick={() => toggleSelection(sim.id)}
                data-testid={`select-simulation-${sim.id}`}
              >
                <Checkbox checked={selectedIds.includes(sim.id)} onCheckedChange={() => toggleSelection(sim.id)} />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-slate-900 dark:text-white truncate">{sim.name}</p>
                  <p className="text-xs text-slate-500 dark:text-slate-400">{new Date(sim.created_at).toLocaleDateString()}</p>
                </div>
                {selectedIds.includes(sim.id) && <CheckCircle2 className="w-4 h-4 text-blue-500 flex-shrink-0" />}
              </div>
            ))}
          </CardContent>
        </Card>

        {/* ---- Results Panel ---- */}
        <Card className="lg:col-span-3 border border-slate-200/80 dark:border-border">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <CardTitle className="text-base flex items-center gap-2">
                <BarChart3 className="w-5 h-5 text-slate-500 dark:text-slate-400" />
                Comparison Results
                {comparison && (
                  <Badge variant="secondary" className="text-[10px] ml-1">
                    {comparison.simulation_count} simulations
                  </Badge>
                )}
              </CardTitle>
              {selectedIds.length >= 2 && availableDemographics.length > 0 && (
                <div className="flex items-center gap-2">
                  <Select value={selectedDemographic} onValueChange={setSelectedDemographic}>
                    <SelectTrigger className="w-40 h-8 text-xs" data-testid="demographic-select">
                      <SelectValue placeholder="By demographic" />
                    </SelectTrigger>
                    <SelectContent>
                      {availableDemographics.map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <Button
                    size="sm" variant="outline" className="h-8"
                    onClick={handleDemographicCompare}
                    disabled={!selectedDemographic || comparing}
                    data-testid="demographic-compare-btn"
                  >
                    <Users className="w-3.5 h-3.5 mr-1.5" />
                    Compare
                  </Button>
                </div>
              )}
            </div>
          </CardHeader>

          <CardContent>
            {(comparison || demographicComparison) ? (
              <Tabs value={activeTab} onValueChange={setActiveTab}>
                <TabsList className="grid w-full grid-cols-3 mb-4" data-testid="compare-tabs">
                  <TabsTrigger value="distribution" disabled={!comparison}>Distribution</TabsTrigger>
                  <TabsTrigger value="statistics" disabled={!comparison}>Statistics</TabsTrigger>
                  <TabsTrigger value="demographic" disabled={!demographicComparison}>
                    By {demographicComparison?.demographic || 'Demographic'}
                  </TabsTrigger>
                </TabsList>

                {/* ==================== DISTRIBUTION TAB ==================== */}
                <TabsContent value="distribution" className="space-y-6" data-testid="distribution-tab">
                  {comparison && (
                    <>
                      {/* Chart mode selector */}
                      <div className="flex items-center gap-2">
                        {['stacked', 'grouped', 'radar'].map(mode => (
                          <Button
                            key={mode}
                            size="sm"
                            variant={chartMode === mode ? 'default' : 'outline'}
                            className={cn('h-7 text-xs capitalize', chartMode === mode && 'bg-primary')}
                            onClick={() => setChartMode(mode)}
                            data-testid={`chart-mode-${mode}`}
                          >
                            {mode}
                          </Button>
                        ))}
                        <TooltipProvider>
                          <UITooltip>
                            <TooltipTrigger asChild>
                              <Info className="w-3.5 h-3.5 text-slate-400 cursor-help" />
                            </TooltipTrigger>
                            <TooltipContent side="right" className="max-w-[200px] text-xs">
                              Stacked shows full distribution per simulation. Grouped compares by response. Radar overlays profiles.
                            </TooltipContent>
                          </UITooltip>
                        </TooltipProvider>
                      </div>

                      {/* ----- Stacked Horizontal Bars ----- */}
                      {chartMode === 'stacked' && (
                        <ResponsiveContainer width="100%" height={Math.max(180, comparison.comparisons.length * 60 + 40)}>
                          <BarChart data={stackedData} layout="vertical" barCategoryGap="20%">
                            <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" horizontal={false} />
                            <XAxis type="number" domain={[0, 100]} unit="%" tick={{ fontSize: 11 }} />
                            <YAxis
                              type="category" dataKey="name" width={140}
                              tick={{ fontSize: 11 }} tickLine={false}
                            />
                            <Tooltip content={<StackedTooltip />} />
                            <Legend
                              wrapperStyle={{ fontSize: 11 }}
                              formatter={(value) => <span className="text-slate-600 dark:text-slate-300">{value}</span>}
                            />
                            {orderedOptions.map((opt, idx) => (
                              <Bar key={opt} dataKey={opt} stackId="stack" fill={scaleColors[idx]} radius={idx === orderedOptions.length - 1 ? [0, 4, 4, 0] : [0, 0, 0, 0]} />
                            ))}
                          </BarChart>
                        </ResponsiveContainer>
                      )}

                      {/* ----- Grouped Bars (scale-colored) ----- */}
                      {chartMode === 'grouped' && (
                        <ResponsiveContainer width="100%" height={320}>
                          <BarChart data={groupedData} barCategoryGap="15%">
                            <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                            <XAxis dataKey="response" tick={{ fontSize: 11 }} />
                            <YAxis unit="%" tick={{ fontSize: 11 }} />
                            <Tooltip content={<GroupedTooltip />} />
                            <Legend wrapperStyle={{ fontSize: 11 }} />
                            {comparison.comparisons.map((c, idx) => (
                              <Bar key={c.simulation.id} dataKey={c.simulation.name} fill={SIM_COLORS[idx % SIM_COLORS.length]} radius={[3, 3, 0, 0]} />
                            ))}
                          </BarChart>
                        </ResponsiveContainer>
                      )}

                      {/* ----- Radar ----- */}
                      {chartMode === 'radar' && (
                        <ResponsiveContainer width="100%" height={360}>
                          <RadarChart data={radarData} outerRadius="75%">
                            <PolarGrid stroke="#E2E8F0" />
                            <PolarAngleAxis dataKey="option" tick={{ fontSize: 10, fill: '#64748B' }} />
                            <PolarRadiusAxis angle={30} domain={[0, 'auto']} tick={{ fontSize: 9 }} />
                            {comparison.comparisons.map((c, idx) => (
                              <Radar
                                key={c.simulation.id}
                                name={c.simulation.name}
                                dataKey={c.simulation.name}
                                stroke={SIM_COLORS[idx % SIM_COLORS.length]}
                                fill={SIM_COLORS[idx % SIM_COLORS.length]}
                                fillOpacity={0.15}
                                strokeWidth={2}
                              />
                            ))}
                            <Legend wrapperStyle={{ fontSize: 11 }} />
                            <Tooltip formatter={(v) => `${Number(v).toFixed(1)}%`} />
                          </RadarChart>
                        </ResponsiveContainer>
                      )}

                      {/* ----- Scale Legend ----- */}
                      <div className="flex items-center gap-1.5 justify-center flex-wrap mt-2">
                        <span className="text-[10px] text-slate-400 mr-1">Scale:</span>
                        {orderedOptions.map((opt, idx) => (
                          <div key={opt} className="flex items-center gap-1">
                            <div className="w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: scaleColors[idx] }} />
                            <span className="text-[10px] text-slate-500 dark:text-slate-400">{opt}</span>
                          </div>
                        ))}
                      </div>

                      {/* ----- Simulation Summary Cards ----- */}
                      <div className={cn('grid gap-4', comparison.comparisons.length <= 3 ? 'grid-cols-1 md:grid-cols-' + comparison.comparisons.length : 'grid-cols-1 md:grid-cols-2 xl:grid-cols-3')}>
                        {comparison.comparisons.map((c, idx) => {
                          const topResponse = Object.entries(c.percentages).sort((a, b) => b[1] - a[1])[0];
                          const topColor = topResponse ? getResponseScaleColor(orderedOptions, topResponse[0]) : '#94A3B8';
                          return (
                            <div
                              key={c.simulation.id}
                              className="p-4 rounded-lg border border-slate-100 dark:border-border bg-white dark:bg-card transition-shadow hover:shadow-sm"
                              data-testid={`sim-card-${c.simulation.id}`}
                            >
                              <div className="flex items-center gap-2 mb-3">
                                <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: SIM_COLORS[idx % SIM_COLORS.length] }} />
                                <h4 className="font-medium text-slate-800 dark:text-slate-100 text-sm truncate">{c.simulation.name}</h4>
                              </div>

                              <div className="grid grid-cols-3 gap-2 mb-3">
                                <div className="text-center p-1.5 rounded bg-slate-50 dark:bg-muted">
                                  <p className="text-[10px] text-slate-400">Responses</p>
                                  <p className="text-sm font-semibold text-slate-800 dark:text-slate-100">{c.total_responses}</p>
                                </div>
                                <div className="text-center p-1.5 rounded bg-slate-50 dark:bg-muted">
                                  <p className="text-[10px] text-slate-400">Mean</p>
                                  <p className="text-sm font-semibold text-slate-800 dark:text-slate-100">{c.mean_score?.toFixed(2) ?? '-'}</p>
                                </div>
                                <div className="text-center p-1.5 rounded bg-slate-50 dark:bg-muted">
                                  <p className="text-[10px] text-slate-400">Entropy</p>
                                  <p className="text-sm font-semibold text-slate-800 dark:text-slate-100">{c.entropy?.toFixed(2) ?? '-'}</p>
                                </div>
                              </div>

                              {/* Mini distribution bar */}
                              <div className="h-3 rounded-full overflow-hidden flex">
                                {orderedOptions.map((opt, oidx) => {
                                  const pct = c.percentages[opt] || 0;
                                  if (pct === 0) return null;
                                  return (
                                    <div
                                      key={opt}
                                      className="h-full transition-all"
                                      style={{ width: `${pct}%`, backgroundColor: scaleColors[oidx] }}
                                      title={`${opt}: ${pct.toFixed(1)}%`}
                                    />
                                  );
                                })}
                              </div>

                              <div className="mt-2 text-xs text-slate-500 dark:text-slate-400 flex items-center gap-1">
                                <span className="font-medium" style={{ color: topColor }}>{topResponse?.[0]}</span>
                                <span>({topResponse?.[1]?.toFixed(1)}%)</span>
                              </div>
                              <div className="mt-1 text-[10px] text-slate-400">
                                {c.population_name} &middot; {c.stimulus_name}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </>
                  )}
                </TabsContent>

                {/* ==================== STATISTICS TAB ==================== */}
                <TabsContent value="statistics" className="space-y-6" data-testid="statistics-tab">
                  {comparison && (
                    <>
                      {/* Summary stats */}
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        <StatCard
                          label="Simulations"
                          value={comparison.simulation_count}
                          icon={GitCompare}
                        />
                        <StatCard
                          label="Pairwise Tests"
                          value={pairwiseTests.length}
                          icon={FlaskConical}
                        />
                        <StatCard
                          label="Significant Pairs"
                          value={pairwiseTests.filter(t => t.significant).length}
                          sub={`of ${pairwiseTests.length} pairs (p<0.05)`}
                          icon={TrendingUp}
                        />
                        <StatCard
                          label="Max Effect Size"
                          value={pairwiseTests.length > 0 ? Math.max(...pairwiseTests.map(t => t.cramers_v)).toFixed(3) : '-'}
                          sub="Cramér's V"
                          icon={ArrowUpDown}
                        />
                      </div>

                      {/* Pairwise comparison table */}
                      <Card className="border border-slate-100 dark:border-border">
                        <CardHeader className="pb-2">
                          <CardTitle className="text-sm flex items-center gap-2">
                            <FlaskConical className="w-4 h-4 text-slate-400" />
                            Pairwise Statistical Tests
                          </CardTitle>
                          <CardDescription className="text-[11px]">
                            Chi-square test of homogeneity with Cramér's V effect size
                          </CardDescription>
                        </CardHeader>
                        <CardContent>
                          {pairwiseTests.length === 0 ? (
                            <p className="text-sm text-slate-400 text-center py-6">No pairwise tests available</p>
                          ) : (
                            <div className="overflow-x-auto">
                              <table className="w-full text-xs" data-testid="pairwise-tests-table">
                                <thead>
                                  <tr className="border-b border-slate-200 dark:border-border">
                                    <th className="text-left p-2 font-semibold text-slate-600 dark:text-slate-300">Simulation A</th>
                                    <th className="text-left p-2 font-semibold text-slate-600 dark:text-slate-300">Simulation B</th>
                                    <th className="text-center p-2 font-semibold text-slate-600 dark:text-slate-300">Chi²</th>
                                    <th className="text-center p-2 font-semibold text-slate-600 dark:text-slate-300">p-value</th>
                                    <th className="text-center p-2 font-semibold text-slate-600 dark:text-slate-300">Cramér's V</th>
                                    <th className="text-center p-2 font-semibold text-slate-600 dark:text-slate-300">Mean Diff</th>
                                    <th className="text-center p-2 font-semibold text-slate-600 dark:text-slate-300">Significance</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {pairwiseTests.map((t, idx) => (
                                    <tr key={idx} className="border-b border-slate-50 dark:border-border/50 hover:bg-slate-50/50 dark:hover:bg-white/[0.02]">
                                      <td className="p-2 font-medium text-slate-700 dark:text-slate-200 max-w-[160px] truncate">{t.sim_a.name}</td>
                                      <td className="p-2 font-medium text-slate-700 dark:text-slate-200 max-w-[160px] truncate">{t.sim_b.name}</td>
                                      <td className="p-2 text-center text-slate-600 dark:text-slate-300">{t.chi2.toFixed(2)}</td>
                                      <td className="p-2 text-center">
                                        <span className={cn(
                                          'font-mono text-[11px]',
                                          t.p_value < 0.001 ? 'text-emerald-700 dark:text-emerald-400 font-semibold' :
                                          t.p_value < 0.05 ? 'text-amber-700 dark:text-amber-400' : 'text-slate-400'
                                        )}>
                                          {t.p_value < 0.001 ? '<0.001' : t.p_value.toFixed(4)}
                                        </span>
                                      </td>
                                      <td className="p-2 text-center">
                                        <div className="flex items-center justify-center gap-1">
                                          <div className="w-12 h-1.5 rounded-full bg-slate-100 dark:bg-slate-700 overflow-hidden">
                                            <div
                                              className="h-full rounded-full transition-all"
                                              style={{
                                                width: `${Math.min(t.cramers_v * 100, 100)}%`,
                                                backgroundColor: t.cramers_v >= 0.5 ? '#EF4444' : t.cramers_v >= 0.3 ? '#F59E0B' : t.cramers_v >= 0.1 ? '#3B82F6' : '#94A3B8'
                                              }}
                                            />
                                          </div>
                                          <span className="text-slate-600">{t.cramers_v.toFixed(3)}</span>
                                        </div>
                                      </td>
                                      <td className="p-2 text-center">
                                        <span className={cn(
                                          'font-mono text-[11px]',
                                          Math.abs(t.mean_diff) > 0.3 ? 'text-blue-700 font-semibold' : 'text-slate-500'
                                        )}>
                                          {t.mean_diff > 0 ? '+' : ''}{t.mean_diff.toFixed(3)}
                                        </span>
                                      </td>
                                      <td className="p-2 text-center">
                                        <SignificanceBadge pValue={t.p_value} cramersV={t.cramers_v} />
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          )}
                        </CardContent>
                      </Card>

                      {/* Interpretation guide */}
                      <div className="p-3 rounded-lg bg-slate-50/80 dark:bg-muted/50 border border-slate-100 dark:border-border text-[11px] text-slate-500 dark:text-slate-400 space-y-1">
                        <p className="font-medium text-slate-600 dark:text-slate-300 mb-1">How to read</p>
                        <p><strong>Chi² test</strong> checks if response distributions differ significantly between two simulations.</p>
                        <p><strong>p-value &lt; 0.05</strong> means the difference is statistically significant (unlikely due to chance).</p>
                        <p><strong>Cramér's V</strong> measures effect size: &lt;0.1 negligible, 0.1-0.3 small, 0.3-0.5 medium, &gt;0.5 large.</p>
                        <p><strong>Mean Diff</strong> shows how ordinal mean scores differ (positive = Sim A leans higher on the scale).</p>
                      </div>
                    </>
                  )}
                </TabsContent>

                {/* ==================== DEMOGRAPHIC TAB ==================== */}
                <TabsContent value="demographic" className="space-y-6" data-testid="demographic-tab">
                  {demographicComparison && (
                    <>
                      <p className="text-sm text-slate-600 dark:text-slate-300">
                        Comparing responses grouped by <strong>{demographicComparison.demographic}</strong>
                      </p>

                      <div className="space-y-6">
                        {demographicComparison.demographic_values.map((demoValue) => {
                          const demoChartData = getDemographicChartData(demoValue);
                          if (demoChartData.length === 0) return null;
                          return (
                            <div key={demoValue} className="space-y-3">
                              <div className="flex items-center gap-2">
                                <Badge variant="outline" className="bg-slate-100/80">
                                  {demographicComparison.demographic}: {demoValue}
                                </Badge>
                                <span className="text-xs text-slate-500">
                                  {demographicComparison.comparisons.map(c => c.breakdown[demoValue]?.total || 0).reduce((a, b) => a + b, 0)} total responses
                                </span>
                              </div>
                              <ResponsiveContainer width="100%" height={200}>
                                <BarChart data={demoChartData}>
                                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                                  <XAxis dataKey="response" tick={{ fontSize: 10 }} />
                                  <YAxis unit="%" tick={{ fontSize: 10 }} />
                                  <Tooltip content={<GroupedTooltip />} />
                                  <Legend wrapperStyle={{ fontSize: 11 }} />
                                  {demographicComparison.comparisons.map((c, idx) => (
                                    <Bar key={c.simulation.id} dataKey={c.simulation.name} fill={SIM_COLORS[idx % SIM_COLORS.length]} radius={[3, 3, 0, 0]} />
                                  ))}
                                </BarChart>
                              </ResponsiveContainer>
                            </div>
                          );
                        })}
                      </div>

                      {/* Summary Table */}
                      <div className="overflow-x-auto">
                        <table className="w-full text-xs" data-testid="demographic-summary-table">
                          <thead>
                            <tr className="border-b border-slate-200">
                              <th className="text-left p-2 font-semibold text-slate-600">{demographicComparison.demographic}</th>
                              {demographicComparison.comparisons.map((c, idx) => (
                                <th key={c.simulation.id} className="text-center p-2 font-semibold text-slate-600">
                                  <div className="flex items-center justify-center gap-1.5">
                                    <div className="w-2 h-2 rounded-full" style={{ backgroundColor: SIM_COLORS[idx % SIM_COLORS.length] }} />
                                    <span className="truncate max-w-[120px]">{c.simulation.name}</span>
                                  </div>
                                </th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {demographicComparison.demographic_values.map(demoValue => (
                              <tr key={demoValue} className="border-b border-slate-100 hover:bg-slate-50/30">
                                <td className="p-2 font-medium text-slate-700">{demoValue}</td>
                                {demographicComparison.comparisons.map(c => {
                                  const breakdown = c.breakdown[demoValue];
                                  if (!breakdown) return <td key={c.simulation.id} className="p-2 text-center text-slate-400">-</td>;
                                  const topResponse = Object.entries(breakdown.percentages || {}).sort((a, b) => b[1] - a[1])[0];
                                  const topColor = topResponse ? getResponseScaleColor(orderedOptions, topResponse[0]) : '#94A3B8';
                                  return (
                                    <td key={c.simulation.id} className="p-2 text-center">
                                      {topResponse ? (
                                        <div className="flex flex-col items-center">
                                          <span className="font-medium" style={{ color: topColor }}>{topResponse[0]}</span>
                                          <span className="text-slate-500">{topResponse[1].toFixed(1)}%</span>
                                          <span className="text-slate-400">n={breakdown.total}</span>
                                        </div>
                                      ) : '-'}
                                    </td>
                                  );
                                })}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </>
                  )}
                </TabsContent>
              </Tabs>
            ) : (
              <div className="h-64 flex flex-col items-center justify-center text-center px-6 text-slate-400 dark:text-slate-500" data-testid="compare-empty-state">
                <GitCompare className="w-10 h-10 mb-3" strokeWidth={1} />
                {selectedIds.length === 0 && (
                  <>
                    <p className="text-sm font-medium text-slate-600 dark:text-slate-300">Select 2 or more simulations to compare</p>
                    <p className="text-xs mt-1.5">A demographic comparison will load automatically once selected.</p>
                  </>
                )}
                {selectedIds.length === 1 && (
                  <>
                    <p className="text-sm font-medium text-slate-600 dark:text-slate-300">Pick one more simulation</p>
                    <p className="text-xs mt-1.5">You need at least 2 to compare.</p>
                  </>
                )}
                {selectedIds.length >= 2 && comparing && (
                  <p className="text-sm">Loading comparison…</p>
                )}
                {selectedIds.length >= 2 && !comparing && availableDemographics.length === 0 && (
                  <p className="text-xs mt-1.5">No common demographics across the selected simulations. Click <strong>Compare</strong> for the overall distribution.</p>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
